// Shared dashboard fetch + background warm for the next pages.
//
// Home, Agents, and App.js all hit ?type=dashboard. Without a shared in-flight
// map, first visit to Home fires the heavy call twice. After it lands we warm
// listings / CRM / sales so those tabs paint from cache.

import api from '../config/api';
import { getDashboardCache, setDashboardCache } from '../config/dashboardCache';
import { getSalesCache, setSalesCache } from '../config/salesCache';
import { getListingsCache, setListingsCache, listingsCacheKey } from '../config/listingsCache';
import { getCrmBoardCache, setCrmBoardCache, crmBoardQueryKey } from '../config/crmBoardCache';
import { seesAgencyListings } from './seesAgencyListings';
import { scheduleIdleRoutePrefetch } from './prefetchAppRoutes';

const _inFlight = new Map();
const _lastWarmed = {};
const _nextWarmed = new Set();
const REWARM_MIN_MS = 30 * 1000;

function storedUser() {
  try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch (_) { return null; }
}

/**
 * Deduped dashboard payload fetch. Cache hit returns immediately.
 * Concurrent callers share one HTTP request.
 */
export function fetchDashboardPayload(userId, { skipCache = false, persist = true } = {}) {
  if (!userId) return Promise.resolve(null);
  const id = String(userId);
  if (!skipCache) {
    const cached = getDashboardCache(id);
    if (cached) return Promise.resolve(cached);
  }
  if (_inFlight.has(id)) return _inFlight.get(id);
  const p = api.get(`/api/users/${id}?type=dashboard`)
    .then((res) => {
      if (persist) {
        setDashboardCache(id, res.data);
        scheduleNextPageWarm();
      }
      return res.data;
    })
    .finally(() => { _inFlight.delete(id); _lastWarmed[id] = Date.now(); });
  _inFlight.set(id, p);
  return p;
}

const rosterInFlightKey = (id) => `${id}:agents-roster`;

/**
 * Lightweight office roster for the Agents tab (no Home dashboard payload).
 */
export function fetchAgentsRosterPayload(userId, { skipCache = false, persist = true } = {}) {
  if (!userId) return Promise.resolve(null);
  const id = String(userId);
  const flightKey = rosterInFlightKey(id);
  if (!skipCache) {
    const cached = getDashboardCache(id);
    const topAgents = cached?.stats?.topAgents || cached?.agentStats?.topAgents;
    if (cached && Array.isArray(topAgents)) return Promise.resolve(cached);
  }
  if (_inFlight.has(flightKey)) return _inFlight.get(flightKey);
  const p = api.get(`/api/users/${id}?type=agents-roster`)
    .then((res) => {
      if (persist) {
        const prev = getDashboardCache(id) || {};
        setDashboardCache(id, {
          ...prev,
          branches: res.data?.branches ?? prev.branches,
          teams: res.data?.teams ?? prev.teams,
          stats: { ...(prev.stats || {}), ...(res.data?.stats || {}), topAgents: res.data?.stats?.topAgents },
          agentStats: { ...(prev.agentStats || {}), ...(res.data?.agentStats || {}), topAgents: res.data?.agentStats?.topAgents },
        });
      }
      return res.data;
    })
    .finally(() => { _inFlight.delete(flightKey); });
  _inFlight.set(flightKey, p);
  return p;
}

/**
 * Warm the dashboard cache for a user in the background. Safe to call
 * repeatedly - it self-throttles (in-flight + recently-warmed guards) and never
 * throws. No-ops when a fresh cache entry already exists.
 */
export function prewarmDashboard(userId) {
  if (!userId) return;
  const id = String(userId);
  if (_inFlight.has(id)) return;
  if (getDashboardCache(id)) {
    scheduleNextPageWarm();
    return;
  }
  if (_lastWarmed[id] && Date.now() - _lastWarmed[id] < REWARM_MIN_MS) return;
  fetchDashboardPayload(id).catch(() => {});
}

function scheduleNextPageWarm() {
  const user = storedUser();
  const id = user?._id ? String(user._id) : '';
  if (!id || _nextWarmed.has(id)) return;
  _nextWarmed.add(id);
  scheduleIdleRoutePrefetch();
  const run = () => {
    const isAgency = seesAgencyListings(user);
    const listingsKey = listingsCacheKey(id, isAgency);
    if (!getListingsCache(listingsKey)) {
      api.get(`/api/users/${id}?type=listings&thumbs=1`)
        .then((res) => {
          const agentProps = Array.isArray(res.data?.agentProperties) ? res.data.agentProperties : [];
          const topAgents = res.data?.stats?.topAgents || res.data?.agentStats?.topAgents || [];
          const nameMap = {};
          (topAgents || []).forEach((a) => {
            const aid = a._id || a.id;
            if (aid) nameMap[String(aid)] = a.name || a.email || 'Agent';
          });
          const leads = res.data?.stats?.crmLeads || res.data?.agentStats?.crmLeads || [];
          setListingsCache(listingsKey, {
            listings: agentProps,
            stats: null,
            agentNames: nameMap,
            crmLeads: Array.isArray(leads) ? leads : [],
          });
        })
        .catch(() => {});
    }
    const crmKey = crmBoardQueryKey({ hideConverted: true });
    if (!getCrmBoardCache(id, crmKey)) {
      api.get(`/api/crm/board?${crmKey}`)
        .then((res) => { setCrmBoardCache(id, crmKey, res.data); })
        .catch(() => {});
    }
    const role = String(user?.role || '').toLowerCase();
    const salesAccount = role === 'agency' || role === 'agency_agent' || role === 'independent_agent' || role === 'agent';
    if (salesAccount && !getSalesCache(id)) {
      api.get('/api/agency/sales-deals')
        .then((res) => {
          const list = Array.isArray(res.data?.deals) ? res.data.deals : [];
          setSalesCache(id, { ...(getSalesCache(id) || {}), deals: list });
        })
        .catch(() => {});
    }
  };
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(run, { timeout: 4000 });
  } else {
    setTimeout(run, 400);
  }
}
